import FlowActions from "components/FlowActions";
import React, { useEffect, useState } from "react";
import {
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import BookDetailsFields from "./BookDetailsFields";
import BookIdentificationFields from "./BookIdentificationFields";
import bookCategories from "data/BookCategoriesList/index.json";
import CategorySuggestions from "components/CategorySuggestions";
import { getApprovedCategories } from "services/categories";
import {
  getBookPhysicalData,
  getPublicationValidationErrors,
  publicationYear,
} from "shared/bookPublication";

const requiredBadgeSx = {
  display: "inline-flex",
  alignItems: "center",
  ml: 1,
  px: 1.1,
  height: 22,
  borderRadius: "6px",
  bgcolor: "#fff0bd",
  color: "#6f4f00",
  fontSize: 11,
  fontWeight: 700,
};

const sectionSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    bgcolor: "#fff",
    transition: "box-shadow 160ms ease, border-color 160ms ease",
    "& fieldset": {
      borderColor: "#d8dee6",
    },
    "&:hover fieldset": {
      borderColor: "#98a7b7",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(8, 127, 35, 0.12)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#087f23",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
  "& textarea": {
    lineHeight: 1.55,
  },
};

const compactFieldSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: 46,
  },
};

const personRoleOptions = [
  "Autor(a)",
  "Ilustrador(a)",
  "Tradutor(a)",
  "Coordenador(a)",
  "Fotógrafo(a)",
  "Revisor(a)",
  "Outros(as)",
];

const mainCategoryOptions = Object.keys(bookCategories);

function getCategoryItems(mainCategory, approvedCategories = []) {
  const categoryGroup = bookCategories[mainCategory] || {};

  const existing = Object.entries(categoryGroup).flatMap(([category, subcategories]) => [
    {
      label: category,
      value: category,
      category,
      isParent: true,
    },
    ...(Array.isArray(subcategories)
      ? subcategories.map((subcategory) => ({
          label: subcategory,
          value: `${category} / ${subcategory}`,
          category,
          isParent: false,
        }))
      : []),
  ]);
  const knownNames = new Set(existing.map((item) => item.value.toLocaleLowerCase("pt-BR")));
  return [
    ...existing,
    ...approvedCategories
      .filter(
        (item) =>
          item.mainCategory === mainCategory &&
          !knownNames.has(item.name.toLocaleLowerCase("pt-BR"))
      )
      .map((item) => ({ label: item.name, value: item.name, category: item.name, isParent: true })),
  ];
}

function CategoryDialog({ open, selectedCategories, onClose, onAdd, bookTitle }) {
  const [mainCategory, setMainCategory] = useState(mainCategoryOptions[0] || "");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [approvedCategories, setApprovedCategories] = useState([]);
  const [catalogError, setCatalogError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setCatalogError("");
    getApprovedCategories()
      .then((result) => {
        if (active) setApprovedCategories(result.categories || []);
      })
      .catch(() => {
        if (active)
          setCatalogError(
            "Não foi possível carregar as categorias adicionais. As categorias padrão continuam disponíveis."
          );
      });
    return () => {
      active = false;
    };
  }, [open]);

  const categoryItems = getCategoryItems(mainCategory, approvedCategories);
  const canAdd = Boolean(mainCategory && selectedCategory);

  const handleMainCategoryChange = (event) => {
    setMainCategory(event.target.value);
    setSelectedCategory("");
  };

  const handleAdd = () => {
    if (!canAdd) return;

    onAdd({
      main: mainCategory,
      category: selectedCategory,
    });
    setSelectedCategory("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 26px 80px rgba(15, 23, 42, 0.36)",
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(15, 23, 42, 0.68)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "#172033",
          fontWeight: 900,
        }}
      >
        Categoria do Título
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: "#eef2f7" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        {catalogError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {catalogError}
          </Alert>
        )}
        <Typography variant="subtitle2" fontWeight={900} color="#27364a" mb={1}>
          1. Selecione a Categoria Principal
        </Typography>
        <FormControl fullWidth sx={{ ...compactFieldSx, mb: 2.5 }}>
          <Select
            value={mainCategory}
            onChange={handleMainCategoryChange}
            IconComponent={ArrowDropDownIcon}
          >
            {mainCategoryOptions.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" fontWeight={900} color="#27364a" mb={1}>
          2. Selecione a Categoria (e subcategorias, se existir)
        </Typography>
        <Box
          sx={{
            border: "1px solid #d8dee6",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: selectedCategory ? "0 0 0 3px rgba(8, 127, 35, 0.12)" : "none",
          }}
        >
          <List
            dense
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              py: 0.5,
              bgcolor: "#fff",
            }}
          >
            {categoryItems.map((item) => (
              <ListItemButton
                key={item.value}
                selected={selectedCategory === item.value}
                onClick={() => setSelectedCategory(item.value)}
                sx={{
                  minHeight: 34,
                  pl: item.isParent ? 1.5 : 3.5,
                  borderLeft: item.isParent ? "3px solid transparent" : "3px solid #e8edf3",
                  "&.Mui-selected": {
                    bgcolor: "#e5f7e9",
                    borderLeftColor: "#087f23",
                  },
                  "&.Mui-selected:hover": {
                    bgcolor: "#dceaff",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  secondary={!item.isParent ? item.category : null}
                  primaryTypographyProps={{
                    fontSize: 15,
                    fontWeight: item.isParent ? 800 : 500,
                    color: "#27364a",
                  }}
                  secondaryTypographyProps={{
                    fontSize: 11,
                    color: "#7b8794",
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {selectedCategories.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" color="#64748b" fontWeight={800}>
              Categorias adicionadas
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
              {selectedCategories.map((category) => (
                <Chip
                  key={`${category.main}-${category.category}`}
                  size="small"
                  label={`${category.main} / ${category.category}`}
                  sx={{ bgcolor: "#e8f8f0", color: "#16824a", fontWeight: 700 }}
                />
              ))}
            </Box>
          </Box>
        )}
        <CategorySuggestions mainCategory={mainCategory} bookTitle={bookTitle} />
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, bgcolor: "#f8fafc" }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 8,
            px: 2.5,
            color: "#e91e63",
            bgcolor: "#fee7ec",
            textTransform: "none",
            fontWeight: 800,
            "&:hover": { bgcolor: "#fbd0da" },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!canAdd}
          sx={{
            borderRadius: 8,
            px: 3,
            bgcolor: "#087f23",
            textTransform: "none",
            fontWeight: 800,
            boxShadow: "none",
            "&:hover": { bgcolor: "#155fc0", boxShadow: "none" },
          }}
        >
          Adicionar Categoria
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SectionTitle({ children, required = false }) {
  return (
    <Typography variant="subtitle2" fontWeight={800} mb={1.1} color="#27364a">
      {children}
      {required && (
        <Box component="span" sx={requiredBadgeSx}>
          obrigatório
        </Box>
      )}
    </Typography>
  );
}

function SectionCard({ children, sx }) {
  return <Card sx={{ ...sectionSx, mb: 1.5, ...sx }}>{children}</Card>;
}

function TitleInformationForm({
  isSaving = false,
  publicationBlocked = false,
  coverageResult,
  onClose,
  onSave,
  pdfInfo,
  paper,
  user,
  draft,
  onDraftChange,
  editing = false,
  inDialog = false,
  reference,
}) {
  const [form, setForm] = useState(() => ({
    title: pdfInfo?.title || "",
    subtitle: "",
    hasSeries: false,
    collectionName: "",
    collectionVolume: "",
    synopsis: "",
    price: "",
    author: pdfInfo?.author || user?.displayName || "",
    publicationYear: String(publicationYear()),
    edition: "1",
    finish: pdfInfo?.pages > 56 ? "Brochura" : "Grampeado",
    formatType: "Capa comum",
    publisher: "",
    classificationType: "BISAC",
    bisacSubject: "Geral",
    weightGr: "",
    depthCm: "",
    interiorColor: "bw",
    language: "pt-BR",
    isbn: "",
    identificationMode: "pso",
    internalCode: "",
    identifierRequestId: crypto.randomUUID(),
    ageRange: "SEM CLASSIFICAÇÃO",
    keywords: "",
    categories: [],
    ...draft,
    publicationDetailsConfirmed: false,
  }));
  const colorLocked = Boolean(coverageResult?.colored || draft?.coverageAnalysis?.colored);
  useEffect(() => {
    if (colorLocked)
      setForm((current) =>
        current.interiorColor === "color"
          ? current
          : { ...current, interiorColor: "color", publicationDetailsConfirmed: false }
      );
  }, [colorLocked]);
  const [people, setPeople] = useState(draft?.people || []);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const errors = validationAttempted
    ? getPublicationValidationErrors({ ...form, pdf: pdfInfo, paper }, { editing })
    : {};
  if (validationAttempted && !form.categories.length)
    errors.categories = "Adicione ao menos uma categoria para o título.";
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const physical = getBookPhysicalData({ ...form, pdf: pdfInfo, paper });

  useEffect(() => {
    onDraftChange?.({ ...form, people });
  }, [form, people, onDraftChange]);

  const updateField = (field) => (event) => {
    const value = field === "hasSeries" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value, publicationDetailsConfirmed: false }));
  };

  const addPerson = () => {
    setPeople((current) => [...current, { role: "Autor(a)", name: "" }]);
  };

  const updatePerson = (index, field) => (event) => {
    setPeople((current) =>
      current.map((person, personIndex) =>
        personIndex === index ? { ...person, [field]: event.target.value } : person
      )
    );
  };

  const removePerson = (index) => {
    setPeople((current) => current.filter((_, personIndex) => personIndex !== index));
  };

  const handleSave = () => {
    if (publicationBlocked) return;
    setValidationAttempted(true);
    const pending = getPublicationValidationErrors({ ...form, pdf: pdfInfo, paper }, { editing });
    if (!form.categories.length)
      pending.categories = "Adicione ao menos uma categoria para o título.";
    if (Object.keys(pending).length) {
      requestAnimationFrame(() => {
        const target = document.querySelector(".publication-form [data-validation-error='true']");
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
        target
          ?.querySelector("input, textarea, button, [role='combobox']")
          ?.focus({ preventScroll: true });
      });
      return;
    }

    if (typeof onSave === "function") {
      onSave({ ...form, people, interiorColor: colorLocked ? "color" : form.interiorColor });
    }
  };

  const addCategory = (category) => {
    setForm((current) => {
      const alreadyAdded = current.categories.some(
        (item) => item.main === category.main && item.category === category.category
      );

      if (alreadyAdded) {
        return current;
      }

      return {
        ...current,
        categories: [...current.categories, category],
      };
    });
  };

  const removeCategory = (categoryToRemove) => {
    setForm((current) => ({
      ...current,
      categories: current.categories.filter(
        (category) =>
          category.main !== categoryToRemove.main || category.category !== categoryToRemove.category
      ),
    }));
  };

  return (
    <Box
      component={inDialog ? "div" : "fieldset"}
      className="publication-form"
      disabled={inDialog ? undefined : isSaving}
      mt={inDialog ? 0 : 3}
      sx={{
        border: 0,
        minWidth: 0,
        p: 0,
        bgcolor: "#eef1f4",
        minHeight: inDialog ? 0 : "calc(100vh - 170px)",
        pb: inDialog ? 0 : 10,
        ...(inDialog && {
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          m: 0,
          width: "100%",
        }),
      }}
    >
      <Box
        sx={{
          border: 0,
          minWidth: 0,
          m: 0,
          width: "100%",
          maxWidth: 1344,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: 2,
          ...(inDialog && {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }),
        }}
      >
        <Box component="fieldset" disabled={isSaving} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
          <SectionCard
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
              borderLeft: "4px solid #1fc76a",
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={900} color="#172033">
                {editing ? "Editar publicação" : "Informações do Título"}
              </Typography>
              <Typography variant="body2" color="#64748b" mt={0.5}>
                O título, autor(a) e categoria são obrigatórios (em destaque), porém quanto mais
                informações, maior será a visibilidade do seu livro =)
              </Typography>
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip size="small" label="Metadados" sx={{ bgcolor: "#e5f7e9", color: "#087f23" }} />
              <Chip size="small" label="Publicação" sx={{ bgcolor: "#e8f8f0", color: "#16824a" }} />
            </Box>
          </SectionCard>

          <SectionCard>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <SectionTitle required>Título</SectionTitle>
                <TextField
                  fullWidth
                  placeholder="Título do livro"
                  error={Boolean(errors.title)}
                  data-validation-error={Boolean(errors.title)}
                  value={form.title}
                  inputProps={{ maxLength: 255 }}
                  onChange={updateField("title")}
                  helperText={
                    errors.title ||
                    (pdfInfo?.titleSource === "metadata"
                      ? "Sugerido pelos metadados do PDF. Revise o título da obra."
                      : "Sugerido pelo nome do arquivo, quando disponível. Revise o título da obra.")
                  }
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionTitle>Subtítulo</SectionTitle>
                <TextField
                  fullWidth
                  placeholder="Subtítulo do livro"
                  value={form.subtitle}
                  onChange={updateField("subtitle")}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <SectionTitle>Coleção, Série ou Coletânea?</SectionTitle>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    minHeight: 56,
                    px: 1.2,
                    border: "1px solid #e3e8ef",
                    borderRadius: "8px",
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Switch
                    checked={form.hasSeries}
                    onChange={updateField("hasSeries")}
                    color="success"
                  />
                  <Typography variant="body2">Este livro faz parte de uma série?</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <SectionTitle>Nome da Coleção</SectionTitle>
                <TextField
                  fullWidth
                  disabled={!form.hasSeries}
                  placeholder="Coleção"
                  value={form.collectionName}
                  onChange={updateField("collectionName")}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <SectionTitle>Número da Coleção / Volume</SectionTitle>
                <TextField
                  fullWidth
                  disabled={!form.hasSeries}
                  placeholder="Volume"
                  value={form.collectionVolume}
                  onChange={updateField("collectionVolume")}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <SectionTitle>Sinopse / Descrição / Resenha / Resumo</SectionTitle>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  value={form.synopsis}
                  onChange={updateField("synopsis")}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard>
            <Box id="book-details-review" />
            {editing ? (
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Código PSO / referência"
                    value={reference || form.internalCode || ""}
                    disabled
                    helperText="Identificador permanente da obra e de seus pedidos."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ISBN"
                    value={form.isbn || "Sem ISBN"}
                    disabled
                    helperText="A identificação desta publicação é preservada na edição."
                  />
                </Grid>
              </Grid>
            ) : (
              <BookIdentificationFields form={form} setForm={setForm} errors={errors} />
            )}
            <BookDetailsFields
              errors={errors}
              form={form}
              updateField={updateField}
              physical={physical}
              pdfInfo={pdfInfo}
              paper={paper}
              editing={editing}
              interiorColorLocked={colorLocked}
              coveragePending={publicationBlocked}
            />
            <FormControlLabel
              data-validation-error={Boolean(errors.publicationDetailsConfirmed)}
              sx={{
                mt: 2,
                alignItems: "flex-start",
                color: errors.publicationDetailsConfirmed ? "error.main" : undefined,
              }}
              control={
                <Checkbox
                  checked={form.publicationDetailsConfirmed}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      publicationDetailsConfirmed: event.target.checked,
                    }))
                  }
                />
              }
              label="Conferi o título, o autor e os dados editoriais e físicos desta obra. Estou ciente de que peso e profundidade podem ser estimativas."
            />
            {errors.publicationDetailsConfirmed && (
              <Alert severity="error">{errors.publicationDetailsConfirmed}</Alert>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitle>Pessoas Envolvidas (opcional)</SectionTitle>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                {people.map((person, index) => (
                  <Box
                    key={`person-${index}`}
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    mb={1.5}
                  >
                    <Box width={{ xs: "100%", sm: 156 }} flexShrink={0}>
                      <FormControl
                        fullWidth
                        sx={{
                          ...compactFieldSx,
                          "& .MuiOutlinedInput-root": {
                            ...compactFieldSx["& .MuiOutlinedInput-root"],
                            borderRadius: { xs: "8px 8px 0 0", sm: "8px 0 0 8px" },
                          },
                          "& .MuiSelect-select": {
                            pr: "34px !important",
                          },
                          "& .MuiSelect-icon": {
                            display: "block",
                            right: 8,
                            color: "#1f2937",
                            fontSize: 24,
                            pointerEvents: "none",
                          },
                        }}
                      >
                        <Select
                          value={person.role}
                          onChange={updatePerson(index, "role")}
                          IconComponent={ArrowDropDownIcon}
                        >
                          {personRoleOptions.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box flex={1}>
                      <Box display="flex">
                        <TextField
                          fullWidth
                          placeholder="Nome"
                          value={person.name}
                          onChange={updatePerson(index, "name")}
                          sx={{
                            ...compactFieldSx,
                            "& .MuiOutlinedInput-root": {
                              ...compactFieldSx["& .MuiOutlinedInput-root"],
                              borderRadius: { xs: "0 0 0 8px", sm: "0" },
                            },
                          }}
                        />
                        <IconButton
                          onClick={() => removePerson(index)}
                          sx={{
                            width: 46,
                            height: 46,
                            flexShrink: 0,
                            borderRadius: { xs: "0 0 8px 0", sm: "0 8px 8px 0" },
                            bgcolor: "#fee7ec",
                            color: "#e91e63",
                            "&:hover": {
                              bgcolor: "#fbd0da",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12} md={4} display="flex" alignItems="flex-start">
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={addPerson}
                  sx={{
                    borderRadius: 8,
                    color: "#172033",
                    borderColor: "#172033",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Adicionar mais pessoas
                </Button>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard>
            <Box data-validation-error={Boolean(errors.categories)} sx={{ scrollMarginTop: 24 }}>
              <SectionTitle required>Categorias do Título</SectionTitle>
              {errors.categories && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.categories}
                </Alert>
              )}
              {form.categories.length > 0 && (
                <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                  {form.categories.map((category) => (
                    <Chip
                      key={`${category.main}-${category.category}`}
                      label={`${category.main} / ${category.category}`}
                      onDelete={() => removeCategory(category)}
                      sx={{
                        bgcolor: "#e8f8f0",
                        color: "#16824a",
                        fontWeight: 700,
                        "& .MuiChip-deleteIcon": {
                          color: "#16824a",
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setIsCategoryDialogOpen(true)}
                sx={{
                  borderRadius: 8,
                  color: "#172033",
                  borderColor: "#172033",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Adicionar Categorias
              </Button>
            </Box>
          </SectionCard>

          <CategoryDialog
            open={isCategoryDialogOpen}
            bookTitle={form.title}
            selectedCategories={form.categories}
            onClose={() => setIsCategoryDialogOpen(false)}
            onAdd={addCategory}
          />

          <SectionCard sx={{ mb: 0 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <SectionTitle>Faixa Etária</SectionTitle>
                <FormControl fullWidth sx={fieldSx}>
                  <Select value={form.ageRange} onChange={updateField("ageRange")}>
                    <MenuItem value="SEM CLASSIFICAÇÃO">SEM CLASSIFICAÇÃO</MenuItem>
                    <MenuItem value="LIVRE">LIVRE</MenuItem>
                    <MenuItem value="10 ANOS">10 ANOS</MenuItem>
                    <MenuItem value="12 ANOS">12 ANOS</MenuItem>
                    <MenuItem value="14 ANOS">14 ANOS</MenuItem>
                    <MenuItem value="16 ANOS">16 ANOS</MenuItem>
                    <MenuItem value="18 ANOS">18 ANOS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionTitle>Palavras Chave</SectionTitle>
                <TextField
                  fullWidth
                  placeholder="Digite a palavra"
                  value={form.keywords}
                  onChange={updateField("keywords")}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Box>
      </Box>

      <FlowActions
        sx={
          inDialog
            ? {
                position: "static",
                flexShrink: 0,
                mt: 0,
                borderRadius: 0,
                borderLeft: 0,
                borderRight: 0,
                borderBottom: 0,
              }
            : undefined
        }
        onBack={onClose}
        backLabel={editing ? "Cancelar edição" : "Voltar à capa"}
        onContinue={handleSave}
        continueLabel={
          isSaving
            ? editing
              ? "Salvando…"
              : "Publicando…"
            : editing
            ? "Salvar alterações"
            : "Publicar livro"
        }
        busy={isSaving}
        disabled={publicationBlocked}
        hint={
          publicationBlocked
            ? "Aguarde a conclusão da análise de cobertura para publicar. Você pode continuar preenchendo os dados."
            : editing
            ? "Confira os dados antes de salvar as alterações."
            : "Revise os dados e confirme as informações da obra antes de publicar."
        }
      />
    </Box>
  );
}

export default TitleInformationForm;
